import { html, LitElement } from 'lit-element'
import hotkeySettingsStyles from './keyboard-shortcut-settings.styles.scss'
import hotkeySettingsContainerStyles from './keyboard-shortcut-settings-container.styles.scss'
import { t } from '../../directives/translate'
import type { HotkeySection, HotkeyAction } from './keyboard-shortcut.models'
import { repeat } from 'lit-html/directives/repeat'
import {
  getKeyboardShortcutConfiguration,
  KeyCombinationValidator,
} from '../../services/keyboard-shortcut/keyboard-shortcut'
import { KeyboardShortcutUIStructureProvider, nonEditableShortcuts } from './keyboard-shortcut-UI-structure-provider'
import type { KeyboardShortcutConfig } from '@getgo/container-client'
import { getTranslation } from '../../services/i18n/i18nUtils'
import { KeyboardShortcutIdTranslation } from './keyboard-shortcut-translation'
import { isContainer } from '../../services/container/helpers'

export class GoToKeyboardShortcutSettings extends LitElement {
  static readonly tagName = 'goto-keyboard-shortcut-settings'

  private keyCombinationValidator: KeyCombinationValidator = new KeyCombinationValidator()

  connectedCallback() {
    super.connectedCallback()
    this.keyCombinationValidator.fillListOfUsedCombinationsFromConfig(getKeyboardShortcutConfiguration())
  }

  static get styles() {
    const styles = [hotkeySettingsStyles]
    if (isContainer()) {
      styles.push(hotkeySettingsContainerStyles)
    }
    return styles
  }

  private getHotkeySections(): HotkeySection[] {
    const sections: Array<HotkeySection> = []

    const keyboardShortcutConfig = getKeyboardShortcutConfiguration()
    if (!keyboardShortcutConfig) {
      return []
    }

    KeyboardShortcutUIStructureProvider.get().forEach(hotkeyUIStructureSection => {
      const hotkeyAction: Array<HotkeyAction> = hotkeyUIStructureSection.keyboardShortcutIdListShown.map(action => {
        const listKeyConfiguration =
          Object.keys(nonEditableShortcuts).indexOf(action) === -1
            ? keyboardShortcutConfig[action]
            : nonEditableShortcuts[action]
        const localKeyList: Array<KeyboardShortcutConfig> = []
        const globalKeyList: Array<KeyboardShortcutConfig> = []
        if (listKeyConfiguration) {
          listKeyConfiguration.forEach(keyConfiguration =>
            keyConfiguration.global ? globalKeyList.push(keyConfiguration) : localKeyList.push(keyConfiguration),
          )
        }
        return {
          id: action,
          labelTranslated: getTranslation(KeyboardShortcutIdTranslation?.[action] ?? ''),
          globalKeyCombination: globalKeyList,
          localKeyCombination: localKeyList,
          nonEditable: Object.keys(nonEditableShortcuts).indexOf(action) === -1 ? false : true,
        }
      })
      sections.push({
        sectionLabelTranslated: hotkeyUIStructureSection.sectionLabelTranslated,
        hotkeyAction,
      })
    })
    return sections
  }

  private getGlobalHotkeyColumnName() {
    return html`<div class="hotkey-settings-column keyboard-shortcut-settings-column}">
      <chameleon-typography tag="p" variant="body-medium" color="type-color-secondary"
        >${t('Add a global shortcut')}</chameleon-typography
      >
    </div>`
  }

  render() {
    return html` <chameleon-typography class="hotkey-settings-header" tag="h1" variant="heading-large"
        >${t('Keyboard shortcuts')}</chameleon-typography
      >
      <chameleon-typography class="header-description" tag="p" variant="body-medium"
        >${t('Use these quick keystrokes to carry out common actions in your daily work')}</chameleon-typography
      >
      <div class="hotkey-settings-section">
        <chameleon-typography class="hotkey-header-description" tag="h2" variant="heading-medium"
          >${t('Assigned shortcuts')}</chameleon-typography
        >
        <div class="hotkey-settings">
          ${this.getGlobalHotkeyColumnName()}
          <div class="hotkey-settings-row">
            <chameleon-accordion outlined size="large">
              ${repeat(
                this.getHotkeySections(),
                section => `${section.sectionLabelTranslated}_section`,
                section => html`<chameleon-accordion-item chevron-position="right" ?expanded=${true}>
                  <div slot="heading">
                    <chameleon-typography class="hotkey-section-label" tag="h3" variant="heading-small"
                      >${t(section.sectionLabelTranslated)}</chameleon-typography
                    >
                  </div>
                  <goto-keyboard-shortcut-section
                    class="keyboard-shortcuts-section"
                    .hotkeyActionList=${section.hotkeyAction}
                    .keyCombinationValidator=${this.keyCombinationValidator}
                /></chameleon-accordion-item>`,
              )}
            </chameleon-accordion>
          </div>
        </div>
      </div>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToKeyboardShortcutSettings.tagName]: GoToKeyboardShortcutSettings
  }
}
